/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { Redirect, Route, RouteComponentProps, Switch, useLocation } from 'react-router';
import { HomePage } from 'src/appShell/homePage/HomePage';
import { NotFoundRedirect } from 'src/core/components/NotFoundRoute';
import { PrivateRoute } from 'src/core/components/PrivateRoute';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';
import { LuisAppListPage } from 'src/luis/appShell/appListPage/LuisAppListPage';
import { LuisAppPage } from 'src/luis/appShell/appPage/LuisAppPage';
import { MigrationBlockerPage } from 'src/luis/appShell/MigrationBlockerPage';
import { CluMigrationManager } from 'src/luis/verticals/cluMigrationModal/CluMigrationManager';
import { LuisStartupConfig } from 'src/modules/startupConfigs/LuisStartupConfig';

/**
 * Routes to the application page with the given app id and version id.
 *
 * @param props The props received from the router.
 */
const renderLuisAppPage = (props: RouteComponentProps<{ appId: string; versionId: string }>) => {
    const { params } = props.match;

    return <LuisAppPage appId={params.appId} versionId={params.versionId} />;
};

const ShimmerPage = () => (
    <div style={{ padding: 32 }}>
        <ShimmerFactory shimmerCount={10} fullWidth />
    </div>
);

export const LuisRoutes = observer(() => {
    const location = useLocation();
    const { rootStore } = useGlobalState();
    const { isSignedIn } = rootStore.authStore;
    const migrationInfo = rootStore.userMigrationInfoStoreHouse;
    const isInRoot = doesRouteMatchLocation(routeTable.luis.root(), { exact: true, locationToMatch: location.pathname });

    if (isSignedIn && isInRoot) {
        return <Redirect exact from={routeTable.luis.root()} to={routeTable.luis.applicationsPage()} />;
    }

    return (
        <>
            {isSignedIn && <CluMigrationManager />}

            <Switch>
                {migrationInfo.tracker.isRunning && <Route component={ShimmerPage} />}
                {(migrationInfo.items.length > 0 || migrationInfo.tracker.isFailed) && <Route component={MigrationBlockerPage} />}

                <Route exact path={[routeTable.luis.root(), routeTable.luis.home()]} component={HomePage} />
                <PrivateRoute exact path={routeTable.luis.applicationsPage()} component={LuisAppListPage} />
                <PrivateRoute path={routeTable.luis.applicationPage()} render={renderLuisAppPage} />
                <NotFoundRedirect />
            </Switch>

            {isSignedIn && <LuisStartupConfig />}
        </>
    );
});
